import { useRef, useState } from "react";
import { useAgreementFeedbackTypes } from "../../../../_queries";
import { EffektButton, EffektButtonVariant } from "../../../shared/components/EffektButton/EffektButton";
import { AgreementTypes } from "../../shared/lists/agreementList/AgreementList";
import styles from "./StoppedAgreementFeedback.module.scss";
import { addStoppedAgreementFeedback, deleteStoppedAgreementFeedback } from "../../shared/lists/agreementList/_queries";
import { useAuth0 } from "@auth0/auth0-react";
import { getAccessToken } from "../../../../hooks/useApi";
import { toast } from "react-toastify";
import { AlertCircle } from "react-feather";
import { EffektTextInput } from "../../../shared/components/EffektTextInput/EffektTextInput";
import { useDebouncedCallback } from "use-debounce";
interface FeedbackSelection {
  feedbackTypeId: number;
  otherComement?: string;
  backendId: string | undefined;
}
type OperationType = "add" | "delete";
interface QueuedOperation {
  feedbackTypeId: number;
  otherComement?: string;
  type: OperationType;
  backendId?: string; // For delete operations
  timestamp: number;
}
export const StoppedAgreementFeedback = ({
  agreementId,
  KID,
  agreementType
}: {
  title: string;
  text: string;
  agreementId: string;
  KID: string;
  agreementType: AgreementTypes;
}) => {
  const {
    loading,
    isValidating,
    data,
    error
  } = useAgreementFeedbackTypes();
  const {
    getAccessTokenSilently
  } = useAuth0();
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackSelection[]>([]);
  const [otherComment, setOtherComment] = useState<string | undefined>(undefined);
  const operationQueue = useRef<QueuedOperation[]>([]);
  const processingQueue = useRef<boolean>(false);
  const addToQueue = (operation: QueuedOperation) => {
    operationQueue.current.push(operation);
    processQueue();
  };
  const debouncedSetOtherComment = useDebouncedCallback((val: string, feedbackId: number, existingBackendId?: string) => {
    if (val === "" || val === undefined || val === null) {
      if (existingBackendId) {
        addToQueue({
          feedbackTypeId: feedbackId,
          type: "delete",
          timestamp: Date.now(),
          backendId: existingBackendId
        });
      }
    } else {
      addToQueue({
        feedbackTypeId: feedbackId,
        type: "add",
        otherComement: val,
        timestamp: Date.now()
      });
    }
  }, 750, {
    trailing: true
  });
  const processQueue = async () => {
    if (processingQueue.current || operationQueue.current.length === 0) {
      return;
    }
    processingQueue.current = true;
    const operation_0 = operationQueue.current[0];
    try {
      const token = await getAccessToken(getAccessTokenSilently);
      if (operation_0.type === "add") {
        const insertedId = await addStoppedAgreementFeedback(agreementId, KID, agreementType, operation_0.feedbackTypeId, operation_0.otherComement, token);

        // Find the latest operation for this feedbackTypeId
        const latestOp_0 = [...operationQueue.current].reverse().find(op_0 => op_0.feedbackTypeId === operation_0.feedbackTypeId);
        if (latestOp_0?.type === "add") {
          // If latest operation is also an add, we can remove all intermediate operations
          // for this feedbackTypeId and update the UI
          operationQueue.current = operationQueue.current.filter(op_1 => op_1.feedbackTypeId !== operation_0.feedbackTypeId || op_1 === latestOp_0);
          setSelectedFeedback(prev_1 => {
            const existing = prev_1.find(f_0 => f_0.feedbackTypeId === operation_0.feedbackTypeId);
            if (existing) {
              return prev_1.map(f_1 => f_1.feedbackTypeId === operation_0.feedbackTypeId ? {
                ...f_1,
                backendId: insertedId
              } : f_1);
            }
            return [...prev_1, {
              feedbackTypeId: operation_0.feedbackTypeId,
              backendId: insertedId
            }];
          });
        } else {
          // Latest operation is delete, so we need to delete this record
          await deleteStoppedAgreementFeedback(insertedId, KID, token);
        }
      } else {
        // delete operation
        if (operation_0.backendId) {
          await deleteStoppedAgreementFeedback(operation_0.backendId, KID, token);
        }

        // Find the latest operation for this feedbackTypeId
        const latestOp_1 = [...operationQueue.current].reverse().find(op_2 => op_2.feedbackTypeId === operation_0.feedbackTypeId);
        if (latestOp_1?.type === "delete") {
          // If latest operation is also a delete, we can remove all intermediate operations
          // for this feedbackTypeId and update the UI
          operationQueue.current = operationQueue.current.filter(op_3 => op_3.feedbackTypeId !== operation_0.feedbackTypeId || op_3 === latestOp_1);
          setSelectedFeedback(prev_2 => prev_2.filter(f_2 => f_2.feedbackTypeId !== operation_0.feedbackTypeId));
        }
      }
    } catch (error_0) {
      // On error, we only show a toast if this is the latest operation for this feedbackTypeId
      const latestOp = [...operationQueue.current].reverse().find(op => op.feedbackTypeId === operation_0.feedbackTypeId);
      if (operation_0 === latestOp) {
        failureToast(`Kunne ikke ${operation_0.type === "add" ? "legge til" : "slette"} tilbakemelding`);

        // Revert UI state
        if (operation_0.type === "add") {
          setSelectedFeedback(prev => prev.filter(f => f.feedbackTypeId !== operation_0.feedbackTypeId));
        } else {
          if (operation_0.backendId) {
            setSelectedFeedback(prev_0 => [...prev_0, {
              feedbackTypeId: operation_0.feedbackTypeId,
              backendId: operation_0.backendId
            }]);
          }
        }
      }
    } finally {
      // Remove the processed operation
      operationQueue.current = operationQueue.current.slice(1);
      processingQueue.current = false;

      // Process next operation if any
      processQueue();
    }
  };
  const handleFeedbackToggle = (feedbackTypeId: number) => {
    const existingSelection = selectedFeedback.find(f_3 => f_3.feedbackTypeId === feedbackTypeId);
    if (existingSelection) {
      // Queue delete operation
      addToQueue({
        feedbackTypeId,
        type: "delete",
        backendId: existingSelection.backendId ?? undefined,
        timestamp: Date.now()
      });

      // Optimistically update UI
      setSelectedFeedback(prev_3 => prev_3.filter(f_4 => f_4.feedbackTypeId !== feedbackTypeId));
    } else {
      // Queue add operation
      addToQueue({
        feedbackTypeId,
        type: "add",
        timestamp: Date.now()
      });

      // Optimistically update UI
      setSelectedFeedback(prev_4 => [...prev_4, {
        feedbackTypeId,
        backendId: undefined
      }]);
    }
  };
  if (loading) {
    return <div>Loading...</div>;
  }
  if (error) {
    return <div>Error: {error.message}</div>;
  }
  if (!data) {
    return <div>No data</div>;
  }
  return <div>
      <div>
        <h5>Avtale avsluttet</h5>
        <p>
          Vi har avsluttet din avtale. Det er nyttig for oss om du ønsker å si noe om hvorfor du
          avslutter din avtale, slik at vi kan utvikle oss videre.
        </p>
        <div className={styles.grid}>
          {data.filter(d => !d.isOther).map((feedbackType: {
          ID: number;
          name: string;
        }) => {
          return <EffektButton key={feedbackType.ID} squared onClick={() => handleFeedbackToggle(feedbackType.ID)} selected={selectedFeedback.some(f_5 => f_5.feedbackTypeId === feedbackType.ID)} variant={EffektButtonVariant.SECONDARY}>
                  {feedbackType.name}
                </EffektButton>;
        })}
        </div>
        <div className={styles.otherFeedback}>
          {data.filter(d_0 => d_0.isOther).map((feedbackType_0: {
          ID: number;
          name: string;
        }) => {
          return <>
                  <label>{feedbackType_0.name}:</label>
                  <EffektTextInput key={feedbackType_0.ID} value={otherComment} onChange={val_0 => {
              const existingSelection_0 = selectedFeedback.find(f_6 => f_6.feedbackTypeId === feedbackType_0.ID);
              if (existingSelection_0) {
                setSelectedFeedback(prev_5 => prev_5.map(f_7 => f_7.feedbackTypeId === feedbackType_0.ID ? {
                  ...f_7,
                  otherComement: val_0
                } : f_7));
                debouncedSetOtherComment(val_0, feedbackType_0.ID, existingSelection_0.backendId);
              } else {
                setSelectedFeedback(prev_6 => [...prev_6, {
                  feedbackTypeId: feedbackType_0.ID,
                  otherComement: val_0,
                  backendId: undefined
                }]);
                debouncedSetOtherComment(val_0, feedbackType_0.ID);
              }
            }} />
                </>;
        })}
        </div>
      </div>
    </div>;
};
const failureToast = (msg: string) => toast.error(msg, {
  icon: <AlertCircle size={24} color={"black"} />
});